import { Center, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { FormikHelpers } from "formik";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useRelayEnvironment } from "react-relay";
import { useNavigate } from "react-router-dom";
import { fetchQuery } from "relay-runtime";

import { PopupImportContainerEnqueueMutation } from "~/src/__relay_artifacts__/PopupImportContainerEnqueueMutation.graphql";
import {
  PopupImportContainerFunnelsQuery,
  type PopupImportContainerFunnelsQuery$data,
} from "~/src/__relay_artifacts__/PopupImportContainerFunnelsQuery.graphql";
import { PopupImportContainerMetadataQuery } from "~/src/__relay_artifacts__/PopupImportContainerMetadataQuery.graphql";
import { GhostButton } from "~/src/components/common/Button";
import { useMutationCommit } from "~/src/lib/react-relay";
import { formatUnknownError } from "~/src/lib/utils/formatUnknownError";

import { rowValuesToInput } from "./convertFunctions";
import {
  POPUP_IMPORT_FUNNELS_MAX_PAGES,
  POPUP_IMPORT_FUNNELS_PAGE_SIZE,
  type PopupImportFunnelOption,
  buildPopupImportFunnelOptions,
} from "./popupImportFunnelOptions";
import { PopupImportFunnelOptionsProvider } from "./PopupImportFunnelOptionsContext";
import {
  type PopupImportScheduleOption,
  buildPopupImportScheduleOptions,
  resolveScheduleLabelsToIds,
} from "./popupImportScheduleOptions";
import { PopupImportScheduleOptionsProvider } from "./PopupImportScheduleOptionsContext";
import {
  type PopupImportSourceOption,
  buildPopupImportSourceOptions,
} from "./popupImportSourceOptions";
import { PopupImportSourceOptionsProvider } from "./PopupImportSourceOptionsContext";
import { applyPopupImportVerificationResolution } from "./popupImportVerificationResolution";
import { PopupImportForm } from "./presentations";
import {
  PopupImportFormValues,
  PopupImportRowValues,
} from "./validationSchema";

export type Props = {
  siteSlug: string;
  initialPopups?: PopupImportRowValues[];
  /** Max 画像N (1-based) with data across the parsed CSV; drives default visible image columns. */
  initialCsvMaxImageSlot?: number;
};

const metadataQuery = graphql`
  query PopupImportContainerMetadataQuery($siteSlug: String!) {
    sources(first: 500) {
      edges {
        node {
          id
          name
          slug
        }
      }
    }
    site(slug: $siteSlug) {
      id
      owner {
        schedules(first: 500, orderBy: { field: START_TIME, direction: ASC }) {
          edges {
            node {
              id
              startTime
              endTime
            }
          }
        }
      }
    }
  }
`;

const funnelsQuery = graphql`
  query PopupImportContainerFunnelsQuery(
    $siteSlug: String!
    $first: Int!
    $after: String
  ) {
    site(slug: $siteSlug) {
      funnels(
        first: $first
        after: $after
        withArchived: true
        orderBy: { field: NAME, direction: ASC }
      ) {
        edges {
          node {
            id
            slug
            name
            landingPage {
              id
              url
              name
            }
            formPage {
              id
              url
              name
            }
            confirmPage {
              id
              url
              name
            }
            thanksPage {
              id
              url
              name
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

type FunnelImportNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        PopupImportContainerFunnelsQuery$data["site"]
      >["funnels"]["edges"]
    >[number]
  >["node"]
>;

const enqueueMutation = graphql`
  mutation PopupImportContainerEnqueueMutation(
    $input: EnqueuePopupImportInput!
  ) {
    enqueuePopupImport(input: $input) {
      popupImportJob {
        slug
        status
        totalCount
      }
    }
  }
`;

const LOAD_FUNNELS_MISSING_MESSAGE =
  "サイトまたはファネル一覧を取得できませんでした。";

const LOAD_FUNNELS_TRUNCATED_MESSAGE =
  "ファネルの件数が多いため、一覧の読み込みが上限で打ち切られました。ファネル選択に一部表示されない項目がある場合があります。";

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      siteId: string;
      funnelOptions: PopupImportFunnelOption[];
      scheduleOptions: PopupImportScheduleOption[];
      sourceOptions: PopupImportSourceOption[];
    };

export const PopupImportContainer: FC<Props> = ({
  siteSlug,
  initialPopups,
  initialCsvMaxImageSlot,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const environment = useRelayEnvironment();
  const [loadState, setLoadState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1. Fetch sources + schedules + siteId once (not inside the pagination loop).
        const metaRaw = await fetchQuery<PopupImportContainerMetadataQuery>(
          environment,
          metadataQuery,
          { siteSlug }
        ).toPromise();

        if (cancelled) return;

        if (!metaRaw?.site) {
          toast({
            title: "ポップアップインポートの準備に失敗しました",
            description: LOAD_FUNNELS_MISSING_MESSAGE,
            status: "error",
            duration: 8000,
            isClosable: true,
          });
          setLoadState({ status: "error" });
          return;
        }

        const siteId = metaRaw.site.id;
        const sourceOptions = metaRaw.sources?.edges
          ? buildPopupImportSourceOptions(metaRaw.sources.edges)
          : [];
        const scheduleOptions = metaRaw.site.owner?.schedules?.edges
          ? buildPopupImportScheduleOptions(metaRaw.site.owner.schedules.edges)
          : [];

        // 2. Paginate funnels in a separate, lightweight query.
        const funnelNodes: FunnelImportNode[] = [];
        let after: string | null = null;
        let truncated = false;

        for (let page = 0; page < POPUP_IMPORT_FUNNELS_MAX_PAGES; page++) {
          const raw = await fetchQuery<PopupImportContainerFunnelsQuery>(
            environment,
            funnelsQuery,
            {
              siteSlug,
              first: POPUP_IMPORT_FUNNELS_PAGE_SIZE,
              after,
            }
          ).toPromise();

          if (cancelled) return;
          const data = raw as PopupImportContainerFunnelsQuery$data | undefined;
          if (!data?.site?.funnels) {
            toast({
              title: "ポップアップインポートの準備に失敗しました",
              description: LOAD_FUNNELS_MISSING_MESSAGE,
              status: "error",
              duration: 8000,
              isClosable: true,
            });
            setLoadState({ status: "error" });
            return;
          }

          const conn = data.site.funnels;
          const edges = conn.edges ?? [];
          for (const e of edges) {
            if (e?.node) funnelNodes.push(e.node);
          }
          if (!conn.pageInfo?.hasNextPage) {
            break;
          }
          after = conn.pageInfo.endCursor ?? null;
          if (page === POPUP_IMPORT_FUNNELS_MAX_PAGES - 1) {
            truncated = true;
            break;
          }
        }

        if (cancelled) return;
        if (truncated) {
          toast({
            title: "ファネル一覧の読み込みが上限に達しました",
            description: LOAD_FUNNELS_TRUNCATED_MESSAGE,
            status: "warning",
            duration: 12000,
            isClosable: true,
          });
        }
        setLoadState({
          status: "ready",
          siteId,
          funnelOptions: buildPopupImportFunnelOptions(funnelNodes),
          scheduleOptions,
          sourceOptions,
        });
      } catch (err) {
        if (cancelled) return;
        toast({
          title: "ポップアップインポートの準備に失敗しました",
          description: formatUnknownError(err),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        setLoadState({ status: "error" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [environment, siteSlug, toast]);

  const mutate =
    useMutationCommit<PopupImportContainerEnqueueMutation>(enqueueMutation);

  const handleBackToPopupGroups = useCallback(() => {
    navigate(`/sites/${siteSlug}/popup_groups`);
  }, [navigate, siteSlug]);

  const handleSubmit = useCallback(
    async (
      values: PopupImportFormValues,
      _helpers: FormikHelpers<PopupImportFormValues>
    ) => {
      if (loadState.status !== "ready") return;
      const siteId = loadState.siteId;
      try {
        const popups = values.popups.map((r) =>
          rowValuesToInput(r, loadState.sourceOptions)
        );

        const res = await mutate({
          variables: {
            input: {
              siteId,
              popups,
            },
          },
        });

        const jobSlug = res.enqueuePopupImport?.popupImportJob?.slug;

        toast({
          title: "インポートをキューに追加しました",
          description: "バックグラウンドで処理中です。完了後にお知らせします。",
          status: "info",
          duration: 6000,
          isClosable: true,
        });

        navigate(`/sites/${siteSlug}/popup_groups`, {
          replace: false,
          state: { importJobSlug: jobSlug },
        });
      } catch (err) {
        toast({
          title: "インポートの登録に失敗しました",
          description: formatUnknownError(err),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    },
    [loadState, mutate, navigate, siteSlug, toast]
  );

  /** Resolve verification page id + URL before Formik mounts so 実施するページ shows the label on first paint. */
  const resolvedImportPopups = useMemo(() => {
    if (loadState.status !== "ready" || !initialPopups?.length) {
      return initialPopups;
    }

    let popups = initialPopups;

    // Resolve schedule time labels to IDs
    const scheduleResolved = popups.map((row) => {
      const resolved = resolveScheduleLabelsToIds(
        row.group.scheduleIds,
        loadState.scheduleOptions
      );
      if (
        resolved.length !== row.group.scheduleIds.length ||
        resolved.some((id, i) => id !== row.group.scheduleIds[i])
      ) {
        return {
          ...row,
          group: { ...row.group, scheduleIds: resolved },
        };
      }
      return row;
    });
    const scheduleChanged = scheduleResolved.some((r, i) => r !== popups[i]);
    if (scheduleChanged) popups = scheduleResolved;

    const { next, changed } = applyPopupImportVerificationResolution(
      popups,
      loadState.funnelOptions
    );
    return changed ? next : popups;
  }, [initialPopups, loadState]);

  if (loadState.status === "loading") {
    return (
      <Center py="48px">
        <Spinner size="lg" color="gray.500" mr="12px" />
        <Text fontSize="14px" color="gray.600">
          ポップアップインポートの準備中…
        </Text>
      </Center>
    );
  }

  if (loadState.status === "error") {
    return (
      <Center py="48px">
        <VStack spacing="16px" align="center" maxW="480px" px="16px">
          <Text fontSize="14px" color="red.500" textAlign="center">
            ポップアップインポートの準備に失敗しました。CSVファイルを再度アップロードしてください。
          </Text>
          <GhostButton type="button" onClick={handleBackToPopupGroups}>
            戻る
          </GhostButton>
        </VStack>
      </Center>
    );
  }

  return (
    <PopupImportFunnelOptionsProvider funnelOptions={loadState.funnelOptions}>
      <PopupImportScheduleOptionsProvider
        scheduleOptions={loadState.scheduleOptions}
      >
        <PopupImportSourceOptionsProvider
          sourceOptions={loadState.sourceOptions}
        >
          <PopupImportForm
            siteSlug={siteSlug}
            initialPopups={resolvedImportPopups}
            initialCsvMaxImageSlot={initialCsvMaxImageSlot}
            onSubmit={handleSubmit}
          />
        </PopupImportSourceOptionsProvider>
      </PopupImportScheduleOptionsProvider>
    </PopupImportFunnelOptionsProvider>
  );
};
